import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
  useDeleteMessagesThreadStore,
  useMessagesFileStore,
  useMessagesThreadsStore,
  useMessagesThreadStore,
  usePostMessagesStore,
} from '@lib/store';
import { useRouteHook } from '@shared/hooks';
import { PaletteUtils } from '@shared/utils';
import type { PostMessageRequest } from '@shared/api';
import columns from './widgets/columns';
import { AskForDelete, IMessageEditorForm, Message, MessageEditor, MessagesHeader, Sent } from './widgets';
import { Button, Empty, Popup, Spinner, Table } from '@shared/widgets';
import ComposeIcon from '@assets/icons/messages/compose.svg';
import MessageIcon from '@assets/icons/messages/message.svg';
import ArrowIcon from '@assets/icons/messages/arrow.svg';

export const Messages = () => {
  const { threadId } = useParams<{ threadId?: string }>();
  const { back } = useRouteHook();

  const messagesTreadsStore = useMessagesThreadsStore();
  const messagesThreadStore = useMessagesThreadStore();
  const postMessageStore = usePostMessagesStore();
  const messageFileStore = useMessagesFileStore();
  const deleteMessageThreadStore = useDeleteMessagesThreadStore();

  const [isDeleteVisible, setIsDeleteVisible] = useState<boolean>(false);
  const [isEditorVisible, setIsEditorVisible] = useState<boolean>(false);
  const [isSendVisible, setIsSendVisible] = useState<boolean>(false);

  const { t } = useTranslation('messages');

  useEffect(() => {
    messagesTreadsStore.get();
  }, []);

  useEffect(() => {
    if (threadId) messagesThreadStore.get(threadId);
  }, [threadId]);

  const onDeleteVisible = () => setIsDeleteVisible(true);
  const onEditorVisible = () => setIsEditorVisible(true);
  const onDelete = async () => {
    await deleteMessageThreadStore.delete(threadId!);
    setIsDeleteVisible(false);
    back();
    setTimeout(() => messagesTreadsStore.get(), 500);
  };
  const onSaveAttachment = (messageId: string, filename: string) => messageFileStore.get(messageId, filename);
  const onSend = async (data: IMessageEditorForm) => {
    const formData = new FormData();
    if (threadId) formData.append('MessageGroupId', threadId);
    formData.append('Subject', data.subject ? data.subject : messagesThreadStore.data!.subject!);
    formData.append('Body', data.body);
    if (data.attachments) data.attachments.forEach((file) => formData.append('files', file));

    await postMessageStore.post(formData as unknown as PostMessageRequest);
    setIsEditorVisible(false);
    setIsSendVisible(true);
  };
  const onCloseSent = () => {
    setIsSendVisible(false);
    threadId ? messagesThreadStore.get(threadId) : messagesTreadsStore.get();
  };

  return (
    <>
      <MessagesHeader threadId={threadId!} onEditor={onEditorVisible} onDelete={onDeleteVisible} />
      <div className='min-h-[128px]'>
        {threadId && (
          <>
            <div className='align-center flex justify-between h-[65px] py-[10px] px-0'>
              <Link
                className={clsx(
                  'text-[#dc291c] no-underline text-[16px] min-w-[60px] flex items-center gap-0.5',
                  PaletteUtils.isBordierPalette ? '!text-[#dc291c]' : 'text-[#e5c496]',
                )}
                to={'/messages'}
              >
                <ArrowIcon
                  className={clsx('-translate-y-[1px] translate-x-[2px]', PaletteUtils.isBordierPalette ? '!text-[#dc291c]' : 'text-[#e5c496]')}
                />
                {t('buttons.back', { ns: 'common' })}
              </Link>
              <div className='text-[#dadada] flex items-center my-0 mx-[16px] uppercase w-full'>
                {!messagesTreadsStore.loading ? messagesThreadStore.data?.subject || t('thread.breadcrumbs.noSubject') : null}
              </div>
              <Button
                className='max-[840]:hidden'
                caption={t('buttons.delete', { ns: 'common' })}
                active={isDeleteVisible}
                onClick={onDeleteVisible}
              />
              <Button
                className='ml-2.5 gap-2 max-[840]:hidden'
                caption={t('buttons.reply', { ns: 'common' })}
                icon={<ComposeIcon />}
                active={isEditorVisible}
                onClick={onEditorVisible}
              />
            </div>
            <div className='flex flex-col -m-2 max-h-[calc(100vh-350px)] overflow-y-auto pl-21'>
              {messagesThreadStore.data && messagesThreadStore.data.thread
                ? messagesThreadStore.data.thread.map((item, idx) => <Message key={idx} value={item} onSaveAttachment={onSaveAttachment} />)
                : null}
            </div>
          </>
        )}
        {!threadId && (
          <>
            <div className={'items-center max-[840px]:hidden flex justify-between h-[65px] py-[10px] px-0'}>
              <div
                className={clsx(
                  'inline-block text-[20px] font-[900] tracking-[0.1px] mr-[21px] text-left uppercase',
                  PaletteUtils.isBordierPalette ? '!text-[#dc291c]' : 'text-[#e5c496] font-medium',
                )}
              >
                {t('threads.title')}
              </div>
              <Button
                className='leading-10 px-4 gap-3 text-base'
                caption={t('buttons.newMessage')}
                icon={<ComposeIcon />}
                active={isEditorVisible}
                onClick={onEditorVisible}
              />
            </div>
            <Table
              loading={messagesTreadsStore.loading}
              columns={columns.map((column) => ({ ...column, title: column.title ? t(column.title) : undefined }))}
              data={messagesTreadsStore.data!}
              showPaginate={false}
              emptyText={<Empty icon={<MessageIcon />} title={t('threads.table.title')} />}
            />
          </>
        )}
        {messagesTreadsStore.loading && <Spinner className='min-h-[200px]' />}
        <Popup open={isEditorVisible} timeout={600}>
          <MessageEditor
            onSend={onSend}
            threadId={threadId}
            sending={postMessageStore.loading}
            error={postMessageStore.error}
            onClose={() => setIsEditorVisible(false)}
          />
        </Popup>
        <Popup open={isSendVisible} onClose={() => setIsSendVisible(false)} timeout={600}>
          <Sent onClose={onCloseSent} />
        </Popup>
        <Popup open={isDeleteVisible} onClose={() => setIsDeleteVisible(false)} timeout={600}>
          <AskForDelete loading={deleteMessageThreadStore.loading!} onClose={() => setIsDeleteVisible(false)} onAction={onDelete} />
        </Popup>
      </div>
    </>
  );
};
